import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Tlob } from '@ebootis/common';
import { LoginLibService } from '@ebootis/login';
import { NotificationHandlerComponent, StorageService } from '@ebootis/utilities';
import { Observable, take } from 'rxjs';
import { ConfigurationService } from '../../components/configuration-select/configuration.service';
import { ArticleDetailsByOid } from '../../services/Ebootis.Queries/ArticleDetailsByOid';
import { ArticleLobOids } from '../../services/Ebootis.Queries/ArticleLobOids';
import { ArticleProxyImage } from '../../services/Ebootis.Queries/ArticleProxyImage';
import { ArticleProxyThumb } from '../../services/Ebootis.Queries/ArticleProxyThumb';
import { ArticleSearch } from '../../services/Ebootis.Queries/ArticleSearch';
import { ArticleSearchFacets } from '../../services/Ebootis.Queries/ArticleSearchFacets';
import { LobByOid } from '../../services/Ebootis.Queries/LobByOid';
import { ArticleTableI18n } from './components/article-table/ArticleTableI18n';

@Injectable({
	providedIn: 'root',
})
export class ArticleService {

	constructor(
		private m_configurationService: ConfigurationService,
		private m_loginLibService: LoginLibService,
		private m_articleSearchFacets: ArticleSearchFacets,
		private m_articleDetails: ArticleDetailsByOid,
		private m_articleThumb: ArticleProxyThumb,
		private m_articleImage: ArticleProxyImage,
		private m_articleLobOids: ArticleLobOids,
		private m_articleLob: LobByOid,
		private storageService: StorageService,
		private m_snackBar: MatSnackBar,
		private m_translation: ArticleTableI18n,
	) { }



	/**
	 * Liefert das Ergebnis des Queries der ArticleSearchFacets zurück.
	 * @param p_params Parameter der Artikel Facettensuche
	 */
	public searchArticleFacets(p_params: ArticleSearchFacets.Params): Observable<ArticleSearchFacets.Result> {
		return this.m_articleSearchFacets.query(p_params);
	}

	/**
	 * Liefert das Ergenis des Queries ArticleDetailsByOid zurück.
	 * @param p_articleOid Artikel OID
	 */
	public getArticleDetailsByOid(p_articleOid: string): Observable<ArticleDetailsByOid.Result> {
		return this.m_articleDetails.query({ article: p_articleOid, config: this.m_configurationService.SelectedConfigOid });
	}

	/**
	 * Liefert den Lob für einen Thumbnail eines Artikels zurück.
	 * @param p_articleOid Artikel OID
	 */
	public getArticleThumb(p_articleOid): Observable<Tlob> {
		return this.m_articleThumb.query({ article: p_articleOid });
	}

	/**
	 * Liefert die URL für einen Thumbnail eines Artikels zurück.
	 * @param p_articleOid Artikel OID
	 */
	public getArticleThumbUrl(p_articleOid): string {
		return this.m_articleThumb.getLobUrl({ article: p_articleOid });
	}

	/**
	 * Liefert den Lob für ein Bild eines Artikels zurück.
	 * @param p_articleOid Artikel OID
	 */
	public getArticleImage(p_articleOid): Observable<Tlob> {
		return this.m_articleImage.query({ article: p_articleOid });
	}

	/**
	 * Liefert die URL für ein Bild eines Artikels zurück.
	 * @param p_articleOid Artikel OID
	 */
	public getArticleImageUrl(p_articleOid): string {
		return this.m_articleImage.getLobUrl({ article: p_articleOid });
	}

	/**
	 * Setzt eine Liste von Artikel-Oids für den Artikelvergleich in den SessionStorage.
	 * @param p_articleOids Artikeldetails Array
	 */
	public setArticleCompare(p_articleOids: string[]) {
		this.storageService.setItem('session', `${this.m_configurationService.SelectedConfigOid}/ZBSarticleOids`, JSON.stringify(p_articleOids));
	}

	/**
	 * Gibt die Oids der zu vergleichenden Artikel zurück
	 * @return articleOids
	 */
	public getArticleCompare(): string[] {
		return JSON.parse(this.storageService.getItem('session', `${this.m_configurationService.SelectedConfigOid}/ZBSarticleOids`) || '[]');
	}

	/**
	 * Fügt einen Artikel der zu vergleichenden Artikel hinzu
	 * @param p_articleOid 
	 */
	public addArticleCompare(p_articleOid: string): void {
		this.getArticleDetailsByOid(p_articleOid)
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					const articleCompareList = this.getArticleCompare();
					if (articleCompareList?.length < 5) {
						if (!articleCompareList.find((oid) => oid == p_articleOid)) {
							articleCompareList.push(p_articleOid);
							this.setArticleCompare(articleCompareList);
							this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
								data: {
									message: this.m_translation.articleTableSuccessText,
									type: 'success',
								},
								duration: 2000,
							});
						} else {
							this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
								data: {
									message: this.m_translation.articleCompareDoubleText,
									type: 'danger',
								},
								duration: 2000,
							});
						}
					} else {
						this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
							data: {
								message: this.m_translation.articleCompareLengthText,
								type: 'danger',
							},
							duration: 2000,
						});
					}
				}
			});
	}

	/**
	 * Getter für die Größe des Artikelvergleichs.
	 */
	get articleCompareLength(): number {
		let sum: number = 0;
		sum = this.getArticleCompare().length;
		return sum;
	}

	/**
	 * Liefert eine Liste von Artikel Lob OIDs zurück.
	 * @param p_params Artikel OIDs
	 */
	public getArticleLobOids(p_params: ArticleLobOids.Params): Observable<ArticleLobOids.Result[]> {
		return this.m_articleLobOids.query(p_params);
	}

	/**
	 * Liefert einen Artikel Lob OID zurück.
	 * @param p_params Artikel OID
	 */
	public getLobByOid(p_params: LobByOid.Params): string {
		return this.m_articleLob.getLobUrl(p_params);
	}

	/**
	 * Entscheidet anhand von onlyAvailableStock,
	 * Artikelverfügbarkeit und Mindestbestand,
	 * ob der Artikel in den Warenkorb gelegt werden kann.
	 */
	public canArticleGetAddedToBasket(p_onlyAvailableStock: boolean, p_article: ArticleDetailsByOid.Result | ArticleSearch.Article): boolean {
		let canArticleBeAddedToBasket = false;

		// Wenn Basisartikel ist ein hinzufügen zum Warenkorb nicht möglich
		if (p_article.isVariantBase)
			return false;

		if (p_onlyAvailableStock && p_article.availableNow != null) { //Streckenartikel haben keine Bestandsdaten Set Artikel u.U schon
			if (p_article.availableNow > 0) {
				//Verfügbar, Mindestbestand ansehen
				if (p_article.salesAmountMin) {
					// Mindestbestand gesetzt, mit Verfügbarkeit vergleichen
					if (p_article.availableNow >= p_article.salesAmountMin) {
						canArticleBeAddedToBasket = true;
					} else {
						canArticleBeAddedToBasket = false;
					}
				} else {
					// kein Mindestbestand gesetzt
					canArticleBeAddedToBasket = true;
				}
			} else {
				// nicht verfügbar
				canArticleBeAddedToBasket = false;
			}
		} else {
			canArticleBeAddedToBasket = true;
		}
		if (p_article.freeContingent != undefined)
			canArticleBeAddedToBasket = p_article.freeContingent > 0;
		return canArticleBeAddedToBasket;
	}

	/**
	 * Ermittelt die Werte für jedes Klassifizierungsattribute und erstellt eine Zuordnung von Attribute-OIDs zu ihren jeweiligen (einmaligen) Werten.
	 * @param p_article Artikel der Varianteninformationen besitzt
	 * @returns Zuordnung von Attribute-OIDs zu ihren jeweiligen (einmaligen) Werten als Map
	 */
	public getClsAttributeValues(p_article: ArticleDetailsByOid.Result): Map<string, string[]> {
		const ret = new Map<string, string[]>();
		p_article?.variantDef?.variants?.forEach((variant) => {
			variant?.values?.forEach((value) => {
				const currentValues = ret.get(value.attributeOid) || [];
				if (!currentValues.includes(value.formatted)) {
					ret.set(value.attributeOid, currentValues.concat(value.formatted));
				}
			});
		});
		return ret;
	}
}
